import { type ReadonlyVec2 } from 'gl-matrix';
import type * as Y from 'yjs';

import {
  type CanvasTransactionOrigin,
  type Transaction,
} from '@canvas/transaction-types';

import { type AnyElement } from './all-element-types.ts';
import {
  type CollaborationTool,
  type CollaborationToolData,
  type CollaborationToolState,
  type CollaborationToolStateData,
  type CollaborationToolType,
} from './collaboration-tools/collaboration-tool.ts';
import { type ElementState, type ElementStateData } from './element-state.ts';
import { type Element, type ElementData, type ElementType } from './element.ts';
import { type Segment } from './elements/path.ts';
import { type PrivateSessionManager } from './private-session-manager.ts';
import { type SlideStore } from './slide-store.ts';
import {
  type TransientTransaction,
  type TransientTransactionTypeMap,
} from './transient-transaction.ts';
import { type TreeStorage } from './tree-storage.ts';
import { type Immutable } from './util.ts';

const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const isValidUuid = (id: string) => uuidRegex.test(id);

export type BoardId = string & { readonly __boardId: unique symbol };

/**
 * ContentId is the ID Confluence uses and should be a long but in the frontend is used as a string.
 * Internally within canvas-server, you shouldn't use ContentId to reference a document, but instead use BoardId.
 * @see {BoardId}
 * More information @see: https://hello.atlassian.net/wiki/spaces/WACTSE/pages/2490799368/Guideline+IDs+across+Confluence+and+Fabric
 */
export type ContentId = string & { readonly __contentId: unique symbol };

export const getBoardNameOrDefault = (name?: string | null) =>
  (name ?? '').trim() === '' ? 'Untitled whiteboard' : (name ?? '');

export interface BoardData {
  elements?: ElementStateData;
  collaborationTools?: CollaborationToolStateData;
}

export interface BoardMetadata {
  version: number;
  documentLastCleanedAt: number;
  privateContentDropped: boolean;
}

export interface BoardGroups {
  getGroupForElement(elementId: string): string | undefined;

  getElementsInGroup(groupId: string): string[];

  onMembershipChange(callback: GroupMembershipChangeCallback): VoidFunction;
}

export interface MutableBoardGroups extends BoardGroups {
  assignElementToGroup(elementId: string, groupId: string): void;

  ungroupElement(elementId: string): void;

  ungroupGroup(groupId: string): void;
}

export interface MutableBoard {
  elements: Readonly<ElementState>;
  collaborationTools: Readonly<CollaborationToolState>;
  readonly meta: BoardMetadata;
  readonly treeStore: TreeStorage;
  readonly slideStore: SlideStore;
  readonly groups: MutableBoardGroups;

  addElement<T extends ElementData>(element: T): void;
  addElements<T extends ElementData>(element: T[]): void;

  addCollaborationTool<T extends CollaborationToolData>(collaborationTool: T): void;
  addCollaborationTools<T extends CollaborationToolData>(collaborationTools: T[]): void;

  /**
   * Transforms an existing element into a different element based on the provided data.
   */
  transformElement(id: string, transformer: (element: Element) => ElementData): void;

  /**
   * Sets multiple element positions at once. This is more efficient than updating
   * multiple element positions individually.
   *
   * @param renderedPositions A map mapping each element ID to its new position. This
   * is no longer being used by new  sessions in favour of @param
   * basisPositionEntries but needs to be passed in to support experiences of
   * users that are still on old versions of the app.
   * @param basisPositions A map mapping each element ID to its new basis
   * position.
   * @param attachmentOffsets A map mapping each element ID to its new attachment
   * offset.
   * @see {@link packages/components/src/components/position-component.ts}
   */
  setElementPositions({
    renderedPositions,
    basisPositions,
    attachmentOffsets,
  }: {
    renderedPositions: Map<string, ReadonlyVec2>;
    basisPositions: Map<string, ReadonlyVec2>;
    attachmentOffsets: Map<string, ReadonlyVec2>;
  }): void;

  /**
   * Sets multiple element rotations at once. Rotations are stored in radians.
   */
  setElementRotations(rotations: Map<string, number>): void;

  /**
   * Sets multiple element paths at once. This is more efficient than updating
   * multiple element paths individually.
   *
   * @param entries A map mapping each element ID to its new path.
   */
  setElementPaths(
    entries: Map<string, { start: ReadonlyVec2; end: ReadonlyVec2; segments: Segment[] }>,
  ): void;

  moveElementZIndexAfter(afterId: string, elementIds: string[]): void;

  /**
   * Sets the index of the given slide in separated world space.
   *
   * @param slideId The ID of the slide to set the index of.
   * @param slideIndex The index to set the slide to.
   *
   * @deprecated Will only exist while V1 boards have not yet been migrated. Callers should ensure
   * they support both V1 (using this method) and V2 (using `moveSlidesToIndex`) worlds.
   */
  setLegacySlideIndex(slideId: string, slideIndex: number): void;

  /**
   * Moves the given slide IDs to the target index in shared world space. Surrounding slide indexes
   * are automatically shifted to maintain contiguous, unique indexes.
   *
   * @param slideIds The IDs of the slides to move.
   * @param targetIndex The index to move the slides to.
   */
  moveSlidesToIndex(slideIds: readonly string[], targetIndex: number): void;

  /**
   * Reserves a zIndex for each of the given element IDs, placing them in front of all
   * existing elements. The given element IDs do not need to yet exist, useful for when a
   * group of elements need to be created asynchronously in a specific order.
   *
   * @param ids The IDs of the elements to reserve.
   */
  reserveZIndices(ids: string[]): void;

  deleteElementById(id: string): void;

  deleteElementsById(ids: string[]): void;

  deleteCollaborationToolById(id: string): void;

  deleteCollaborationToolsById(ids: string[]): void;
}

/**
 * A callback invoked when elements undergo transformation in their type.
 */
export type BoardTransformListenerCallback = (
  /**
   * The original element type.
   */
  from: ElementType,
  /**
   * The transformed, new element.
   */
  element: AnyElement,
  /**
   * The Y transaction which caused the transformation.
   */
  transaction: Transaction,
) => void;

export type BoardListenerCallback = (element: Element, transaction: Transaction) => void;
export type CollaborationToolBoardListenerCallback = (
  collaborationTool: CollaborationTool,
  transaction: Transaction,
) => void;
export type ElementUserVoteCountChangeCallback = (
  collaborationTool: CollaborationTool,
  element: Element,
  accountId: string,
  voteChange: 'increase' | 'decrease' | undefined,
  transaction: Transaction,
) => void;
export type UserVoteCompletedChangeCallback = (
  collaborationTool: CollaborationTool,
  accountId: string,
  transaction: Transaction,
) => void;

export type DeleteListenerCallback = (key: string, transaction: Transaction) => void;
export type CollaborationToolDeleteListenerCallback = (
  key: string,
  type: CollaborationToolType,
  transaction: Transaction,
  title?: string,
) => void;

export type OnPropertyChangeListenerCallback = (
  event: Y.YMapEvent<unknown>,
  element: Element,
  transaction: Transaction,
) => void;

export type GroupMembershipChangeDetails = Map<
  string,
  | { action: 'delete'; oldValue: string }
  | { action: 'update'; oldValue: string; newValue: string }
  | { action: 'add'; newValue: string }
>;

export type GroupMembershipChangeCallback = (
  changes: GroupMembershipChangeDetails,
  transaction: Transaction,
) => void;

export enum DocumentTransformerFormat {
  WDF = 'WDF',
}

export type DocumentTransformerResult<T> = {
  /**
   * The transformed document
   */
  transformedDoc: T;
};

export type DocumentTransformerCallback<T> = (
  yDoc: Y.Doc,
) => DocumentTransformerResult<T>;

export interface Board {
  readonly elements: Immutable<ElementState>;
  readonly collaborationTools: Immutable<CollaborationToolState>;
  readonly meta: Immutable<BoardMetadata>;
  readonly elementsLoaded: boolean;
  readonly clientId: number;
  readonly maxZIndex: number;
  readonly privateSessionManager: PrivateSessionManager;
  readonly treeStore: TreeStorage;
  readonly slideStore: SlideStore;
  readonly groups: BoardGroups;

  /** Returns the nearest existing element ID before the given element in z-order. */
  getPreviousElementIdInZOrder(elementId: string): string | undefined;

  /** Returns the existing element ID with the highest z-index. */
  getHighestZIndexElementId(): string | undefined;

  setElementsLoaded(): void;

  mergeYDoc(yDoc: Y.Doc, transactionOrigin?: any): void;

  /**
   * Allows mutation of data on a board in a single transaction.
   * Property changes are checked internally for strict equality,
   * and a transaction is only sent if at least one property is changed.
   *
   * If you only want to mutate a specific element, use {@link mutateById} instead.
   *
   * @param update - The function to call with the mutable board.
   * You may mutate the board contents inside this callback.
   * @param origin - The origin of the transaction
   * CanvasTransactionOrigin.LocalDefault is default and adds the change to the users undo / redo stack.
   */
  mutate(update: (board: MutableBoard) => void, origin?: CanvasTransactionOrigin): void;

  /**
   * Allows mutation of data on a specific element in a single transaction.
   * This uses .mutate() internally.
   *
   * Property changes are checked internally for strict equality,
   * and a transaction is only sent if at least one property is changed.
   *
   * @param id - The ID of the element to mutate. If not found, the update callback is not called.
   * @param update - The function to call with the mutable board.
   * You may mutate the board contents inside this callback.
   * @param isElementType - An optional function to check if the element is of a specific type.
   * The return type of isElementType is used to determine the element type in `update`
   * so you can access element type specific properties.
   * @param origin - The origin of the transaction
   * CanvasTransactionOrigin.LocalDefault is default and adds the change to the users undo / redo stack.
   */
  mutateById<T extends Element<any>>(
    id: string,
    update: (element: T, mutableBoard: MutableBoard) => void,
    isElementType?: (element: Element<any>) => element is T,
    origin?: CanvasTransactionOrigin,
  ): void;

  /**
   * Executes a transient transaction.
   *
   * The transient transaction allows changes to the board to be broadcast to other users
   * without actually persisting data, via the awareness layer.
   *
   * @param type The type of transaction to execute.
   * @param callback Callback to be called with the transaction object.
   */
  transientTransaction<K extends TransientTransaction>(
    type: K,
    callback: (transaction: TransientTransactionTypeMap[K]) => void,
  ): void;

  onElementMove(callback: BoardListenerCallback): VoidFunction;
  onElementAdd(callback: BoardListenerCallback): VoidFunction;
  onElementDelete(callback: DeleteListenerCallback): VoidFunction;

  onCollaborationToolAdd(callback: CollaborationToolBoardListenerCallback): VoidFunction;
  onCollaborationToolDelete(
    callback: CollaborationToolDeleteListenerCallback,
  ): VoidFunction;

  /**
   * Used for hydrating data with default values from our Y model
   */
  hydrateElements(elements: ElementData<any>[]): ElementData<any>[];

  /**
   * Listener for when elements are transformed from one type to another.
   * @param callback invoked on transform
   */
  onElementTransform(callback: BoardTransformListenerCallback): VoidFunction;

  onPropertyChange(callback: OnPropertyChangeListenerCallback): VoidFunction;
  onNamedPropertyChange(property: string, callback: BoardListenerCallback): VoidFunction;

  // #region Element property change listeners
  onZIndexChange(callback: BoardListenerCallback): VoidFunction;
  onTextChange(callback: BoardListenerCallback): VoidFunction;
  onColorChange(callback: BoardListenerCallback): VoidFunction;
  onSizeChange(callback: BoardListenerCallback): VoidFunction;
  onFontScaleChange(callback: BoardListenerCallback): VoidFunction;
  onTreeOrientationChange(callback: BoardListenerCallback): VoidFunction;
  onIconChange(callback: BoardListenerCallback): VoidFunction;
  onBasisSizeChange(callback: BoardListenerCallback): VoidFunction;
  onBasisPositionChange(callback: BoardListenerCallback): VoidFunction;
  onRotationChange(callback: BoardListenerCallback): VoidFunction;
  onDirectionChange(callback: BoardListenerCallback): VoidFunction;
  onShapeChange(callback: BoardListenerCallback): VoidFunction;
  onKindChange(callback: BoardListenerCallback): VoidFunction;
  onFileIdChange(callback: BoardListenerCallback): VoidFunction;
  onNativeSizeChange(callback: BoardListenerCallback): VoidFunction;
  onImageHashChange(callback: BoardListenerCallback): VoidFunction;
  onIsTransparentChange(callback: BoardListenerCallback): VoidFunction;
  onIsPlaceholderChange(callback: BoardListenerCallback): VoidFunction;
  onLockStatusChange(callback: BoardListenerCallback): VoidFunction;
  onCreatedByChange(callback: BoardListenerCallback): VoidFunction;
  onAuthorIdsChange(callback: BoardListenerCallback): VoidFunction;
  onIsAuthorVisibleChange(callback: BoardListenerCallback): VoidFunction;
  onLayerChange(callback: BoardListenerCallback): VoidFunction;
  onAllowFlexibleWidthChange(callback: BoardListenerCallback): VoidFunction;
  onAttachmentChange(callback: BoardListenerCallback): VoidFunction;
  onPathStartChange(callback: BoardListenerCallback): VoidFunction;
  onPathEndChange(callback: BoardListenerCallback): VoidFunction;
  onPathPresentationChange(callback: BoardListenerCallback): VoidFunction;
  onPathStartCapChange(callback: BoardListenerCallback): VoidFunction;
  onPathEndCapChange(callback: BoardListenerCallback): VoidFunction;
  onStrokeChange(callback: BoardListenerCallback): VoidFunction;
  onStrokeColorChange(callback: BoardListenerCallback): VoidFunction;
  onStrokeStyleChange(callback: BoardListenerCallback): VoidFunction;
  onSourceElementChange(callback: BoardListenerCallback): VoidFunction;
  onSourceAnchorChange(callback: BoardListenerCallback): VoidFunction;
  onTargetAnchorChange(callback: BoardListenerCallback): VoidFunction;
  onTargetElementChange(callback: BoardListenerCallback): VoidFunction;
  onTitleChange(callback: BoardListenerCallback): VoidFunction;
  onTitleWidthChange(callback: BoardListenerCallback): VoidFunction;
  onSectionConfigChange(callback: BoardListenerCallback): VoidFunction;
  onRelationshipIdChange(callback: BoardListenerCallback): VoidFunction;
  onRelationDirectionChange(callback: BoardListenerCallback): VoidFunction;
  onPathWaypointOrderChange(callback: BoardListenerCallback): VoidFunction;
  onPathLabelProportionChange(callback: BoardListenerCallback): VoidFunction;
  onPathOffsetPositionChange(callback: BoardListenerCallback): VoidFunction;
  onStartTimeUTCMilliSecondsChange(callback: BoardListenerCallback): VoidFunction;
  onDurationMilliSecondsChange(callback: BoardListenerCallback): VoidFunction;
  onElapsedTimeMilliSecondsChange(callback: BoardListenerCallback): VoidFunction;
  onPausedChange(callback: BoardListenerCallback): VoidFunction;
  onDropShadowChange(callback: BoardListenerCallback): VoidFunction;
  onTextAlignmentChange(callback: BoardListenerCallback): VoidFunction;
  onVerticalAlignmentChange(callback: BoardListenerCallback): VoidFunction;
  onVersionChange(callback: BoardListenerCallback): VoidFunction;
  onResolveStatusChange(callback: BoardListenerCallback): VoidFunction;
  onFollowUpsChange(callback: BoardListenerCallback): VoidFunction;
  onPointsChange(callback: BoardListenerCallback): VoidFunction;
  onDrawingEnableResizeAndDragChange(callback: BoardListenerCallback): VoidFunction;
  // #endregion

  // #region Collaboration tool property change listeners
  onCollaborationToolSessionActiveChange(
    callback: CollaborationToolBoardListenerCallback,
  ): VoidFunction;
  onCollaborationToolUserVoteCountChange(
    callback: ElementUserVoteCountChangeCallback,
  ): VoidFunction;
  onCollaborationToolCompletedUsersChange(
    callback: UserVoteCompletedChangeCallback,
  ): VoidFunction;
  // #endregion

  runDocumentTransformer<T>(
    transformer: DocumentTransformerCallback<T>,
  ): DocumentTransformerResult<T>;
}
